/**
 * Pure presence helpers for the worker grid: connection health derived from
 * the last heartbeat, human labels for elapsed time, and matching of the
 * owner's auto-approval rules. No I/O and no clock access — callers pass time
 * in so everything here stays deterministic and testable.
 */

export type OperationalStatus =
  | 'idle' | 'working' | 'waiting' | 'blocked' | 'awaiting_approval'
  | 'rotating' | 'offline' | 'verifying' | 'completed' | 'failed';

export type ConnectionState = 'connected' | 'stale' | 'disconnected';

const DEFAULT_STALE_AFTER_MS = 60_000;
const DEFAULT_DISCONNECT_AFTER_MS = 300_000;

/** connected when lastSeen is within staleAfterMs of now; stale when within disconnectAfterMs; else disconnected. */
export function connectionState(input: {
  lastSeenAt: string | null;
  status: OperationalStatus;
  nowMs: number;
  staleAfterMs?: number;
  disconnectAfterMs?: number;
}): ConnectionState {
  if (input.status === 'offline') return 'disconnected';
  if (input.lastSeenAt === null) return 'disconnected';
  const seenMs = Date.parse(input.lastSeenAt);
  if (Number.isNaN(seenMs)) return 'disconnected';
  const ageMs = input.nowMs - seenMs;
  const staleAfterMs = input.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  const disconnectAfterMs = input.disconnectAfterMs ?? DEFAULT_DISCONNECT_AFTER_MS;
  if (ageMs <= staleAfterMs) return 'connected';
  if (ageMs <= disconnectAfterMs) return 'stale';
  return 'disconnected';
}

/**
 * Classify the row that node_presence just returned.
 * presenceReadAtMs is the clock when that read arrived. A later desk tick
 * (laterMs) must not age the same row; the next presence read moves the clock.
 */
export function connectionFromPresenceRead(input: {
  lastSeenAt: string | null;
  status: OperationalStatus;
  presenceReadAtMs: number;
  laterMs?: number;
}): ConnectionState {
  void input.laterMs;
  return connectionState({
    lastSeenAt: input.lastSeenAt,
    status: input.status,
    nowMs: input.presenceReadAtMs,
  });
}

/** "12s ago", "5m ago", "3h ago", "2d ago", "never". Future times read as "just now". */
export function lastSeenLabel(lastSeenAt: string | null, nowMs: number): string {
  if (lastSeenAt === null) return 'never';
  const seenMs = Date.parse(lastSeenAt);
  if (Number.isNaN(seenMs)) return 'never';
  const ageMs = nowMs - seenMs;
  if (ageMs <= 0) return 'just now';
  const seconds = Math.floor(ageMs / 1_000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** The same age as lastSeenLabel, without the trailing "ago": "1m", "3d", "just now", "never". */
export function lastSeenAge(lastSeenAt: string | null, nowMs: number): string {
  const label = lastSeenLabel(lastSeenAt, nowMs);
  return label.endsWith(' ago') ? label.slice(0, -4) : label;
}

export interface ComputerReachability {
  /** True only while connectionState is connected. Stale and disconnected are not in touch. */
  readonly inTouch: boolean;
  readonly reachability: 'in touch' | 'losing touch' | 'out of touch';
  /** The node's own idle or working status, and only while it is in touch. */
  readonly activity: 'idle' | 'working' | null;
  /** "last seen 1m", "last seen 3d", "last seen never". */
  readonly lastSeen: string;
  readonly line: string;
}

/**
 * What the Computers list says about one machine. Reachability and last-seen age
 * always show. Idle and working come from the node's own status and only while
 * connectionState says the machine is in touch.
 */
export function computerReachability(input: {
  status: string;
  lastSeenAt: string | null;
  nowMs: number;
}): ComputerReachability {
  const state = connectionState({
    lastSeenAt: input.lastSeenAt,
    status: input.status as OperationalStatus,
    nowMs: input.nowMs,
  });
  const inTouch = state === 'connected';
  const reachability = state === 'connected' ? 'in touch' : state === 'stale' ? 'losing touch' : 'out of touch';
  const activity = inTouch && (input.status === 'idle' || input.status === 'working') ? input.status : null;
  const lastSeen = `last seen ${lastSeenAge(input.lastSeenAt, input.nowMs)}`;
  const line = [reachability, activity, lastSeen].filter((part) => part !== null).join(' · ');
  return { inTouch, reachability, activity, lastSeen, line };
}

export type WorkerBadge = { label: string; tone: 'neutral' | 'active' | 'attention' | 'success' | 'danger' };

const WORKER_BADGES: ReadonlyMap<string, WorkerBadge> = new Map([
  ['idle', { label: 'Idle', tone: 'neutral' }],
  ['working', { label: 'Working', tone: 'active' }],
  ['waiting', { label: 'Waiting', tone: 'neutral' }],
  ['blocked', { label: 'Blocked', tone: 'danger' }],
  ['awaiting_approval', { label: 'Waiting for approval', tone: 'attention' }],
  ['rotating', { label: 'Rotating session', tone: 'active' }],
  ['offline', { label: 'Offline', tone: 'neutral' }],
  ['verifying', { label: 'Verifying', tone: 'active' }],
  ['completed', { label: 'Finished task', tone: 'success' }],
  ['failed', { label: 'Failed', tone: 'danger' }],
]);

/** Status-bar text for one worker; pending approvals always take precedence. */
export function workerBadge(status: string, pendingApprovals: number): WorkerBadge {
  if (pendingApprovals > 0) return { label: `Waiting for approval (${pendingApprovals})`, tone: 'attention' };
  return WORKER_BADGES.get(status) ?? { label: status, tone: 'neutral' };
}
